import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { formatINR } from "@/api/client";
import { Minus, Plus, Trash2, ShoppingCart, ArrowRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import SeoHead from "@/components/seo/SeoHead";

const Cart = () => {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { t } = useI18n();
  const navigate = useNavigate();
  const shipping = 50;
  const handling = 24;
  const total = subtotal + shipping + handling;

  if (items.length === 0) {
    return (
      <div className="container-fah py-20 text-center" data-testid="cart-empty">
        <SeoHead title="Your Cart" description="Review the items in your shopping cart and proceed to secure checkout on FarmersAgroHub." path="/cart" noindex />
        <ShoppingCart className="w-16 h-16 mx-auto text-muted-foreground" />
        <h2 className="font-display text-2xl font-bold mt-4">{t("Your cart is empty")}</h2>
        <p className="text-muted-foreground mt-2">{t("Browse our catalog and add some products to get started.")}</p>
        <Link to="/products" className="mt-6 inline-block bg-primary text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-primary-hover">{t("Continue Shopping")}</Link>
      </div>
    );
  }

  return (
    <div className="container-fah py-8 sm:py-12" data-testid="page-cart">
      <SeoHead title="Your Cart" description="Review the items in your shopping cart and proceed to secure checkout on FarmersAgroHub." path="/cart" noindex />
      <h1 className="font-display text-3xl font-bold mb-6">{t("Your Cart")} ({items.length} {t("items")})</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {items.map((it) => (
            <div key={it.product_id} className="card-fah p-4 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
              <Link to={`/product/${it.product_id}`} className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden bg-muted shrink-0">
                <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${it.product_id}`} className="font-semibold text-sm sm:text-base hover:text-primary line-clamp-2">{t(it.name)}</Link>
                <div className="text-xs text-muted-foreground mt-0.5">{t(it.unit)} · {t("by")} {it.seller_name}</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-bold">{formatINR(it.price)}</span>
                  {it.mrp > it.price && <span className="text-xs text-muted-foreground line-through">{formatINR(it.mrp)}</span>}
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex items-center border border-border rounded-lg">
                    <button onClick={() => updateQty(it.product_id, it.qty - 1)} className="p-1.5 hover:bg-muted"><Minus className="w-4 h-4" /></button>
                    <span className="w-8 text-center text-sm font-semibold">{it.qty}</span>
                    <button onClick={() => updateQty(it.product_id, it.qty + 1)} className="p-1.5 hover:bg-muted"><Plus className="w-4 h-4" /></button>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} className="text-sm text-destructive hover:underline flex items-center gap-1" data-testid={`remove-${it.product_id}`}>
                    <Trash2 className="w-4 h-4" /> {t("Remove")}
                  </button>
                </div>
              </div>
              <div className="text-right shrink-0 font-bold">{formatINR(it.price * it.qty)}</div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="card-fah p-5 sticky top-24" data-testid="cart-summary">
            <h3 className="font-display font-bold text-lg mb-4">{t("Order Summary")}</h3>
            <div className="space-y-2 text-sm">
              <Row label={t("Subtotal")} value={formatINR(subtotal)} />
              <Row label={t("Shipping")} value={formatINR(shipping)} />
              <Row label={t("Handling Charges")} value={formatINR(handling)} />
              <div className="border-t border-border pt-2 mt-2" />
              <div className="flex justify-between font-bold text-base"><span>{t("Total")}</span><span data-testid="cart-total">{formatINR(total)}</span></div>
            </div>
            <button onClick={() => navigate("/checkout")} className="mt-5 w-full bg-secondary hover:opacity-90 text-secondary-foreground font-bold py-3 rounded-lg flex items-center justify-center gap-2" data-testid="btn-checkout">
              {t("Proceed to Checkout")} <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/products" className="mt-3 block text-center text-sm text-muted-foreground hover:text-primary">{t("Continue Shopping")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span>{value}</span></div>
);

export default Cart;
